import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Box,
} from "@mui/material";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import api from "../../globals/axios";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomButton from "../../components/Custom/CustomButton";
import { useTranslation } from "react-i18next";
import SubmittingPopup from "../../components/Custom/FormSubmitPopup";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../components/Alert";

export default function VerificationTaskDetails() {
  const { actionId } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [actionPerform, setActionPerform] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  // New state variables
  const [comments, setComments] = useState("");
  const [verificationImage, setVerificationImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        const response = await api.get(
          `/vle/pending-tasks/?action_id=${actionId}`
        );
        setTask(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching task details:", error);
        setLoading(false);
        showAlert("Error fetching task details", "error");
      }
    };

    fetchTaskDetails();
  }, [actionId]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setVerificationImage(file);
      setImagePreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, verificationImage: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!comments.trim()) {
      newErrors.comments = "Comments are required";
    }

    if (!verificationImage) {
      newErrors.verificationImage = "Verification image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event, status) => {
    event.preventDefault();
    if (!validateForm()) return;

    setActionPerform(true);

    const formData = new FormData();
    formData.append("action_id", actionId);
    formData.append("verification_status", status);
    formData.append("comments", comments);
    formData.append("verification_image", verificationImage);

    try {
      await api.post(
        "/programs/vle-task-action-update/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      showAlert("success", `Task ${status.toLowerCase()} successfully`);
      setActionPerform(false);
      navigate("/vle/verification-tasks");
    } catch (error) {
      setActionPerform(false);
      const errorMessage =
        error.response?.data?.error || "An error occurred. Please try again.";
      showAlert("error", errorMessage);
    }
  };

  const taskFields = [
    { label: "Program", value: task?.program_name },
    { label: "Farmer", value: task?.farmer_name },
    { label: "Task", value: task?.task_name },
    { label: "Due Date", value: task?.due_date },
  ];

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="md">
          {task && (
            <>
              <CustomTitle>{t("Verification Task Details")}</CustomTitle>
              <Grid container spacing={2} mt={2}>
                {taskFields.map((field, index) => (
                  <Grid item xs={12} key={index}>
                    <Typography variant="subtitle1">
                      <strong>{field.label}:</strong> {field.value || "-"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <form onSubmit={(e) => handleSubmit(e, "ACCEPTED")}>
                <Grid container spacing={2} mt={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      rows={4}
                      label={t("Comments")}
                      variant="outlined"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      error={!!errors.comments}
                      helperText={errors.comments}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="file"
                      variant="outlined"
                      onChange={handleImageChange}
                      error={!!errors.verificationImage}
                      helperText={errors.verificationImage}
                    />
                    {imagePreview && (
                      <Box mt={2}>
                        <img
                          src={imagePreview}
                          alt="Verification Preview"
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <CustomButton
                      type="submit"
                      hideIcon
                      sx={{ mt: 2 }}
                      backgroundColor="#4CAF50"
                      onClick={(e) => handleSubmit(e, "ACCEPTED")}
                    >
                      {t("Accept")}
                    </CustomButton>
                  </Grid>
                  <Grid item xs={6}>
                    <CustomButton
                      hideIcon
                      sx={{ mt: 2 }}
                      backgroundColor="#CF5C5C"
                      onClick={(e) => handleSubmit(e, "REJECTED")}
                    >
                      {t("Reject")}
                    </CustomButton>
                  </Grid>
                </Grid>
              </form>
            </>
          )}
        </Container>
        {actionPerform && <SubmittingPopup title="Progress" />}
      </LoaderContainer>
    </>
  );
}
